import { ConfirmModal } from "@/components/modal/ConfirmModal";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { ServiceDetail } from "@/services/service-master.service";
import { AppColors } from "@/styles/colors";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Flex, Text } from "@chakra-ui/react";
import { Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ServiceMasterForm } from "../components/ServiceMasterForm";
import {
  hasEmptyServiceMasterFormValues,
  mapServiceDetailToFormValues,
} from "../utils/service-master-form";
import {
  ServiceMasterCreationForm,
  serviceMasterCreationSchema,
} from "../create/schema/service-master-creation.schema";
import {
  deleteServiceHandler,
  fetchServiceHandler,
  updateServiceHandler,
} from "./schema/handler-functions";

export const ServiceMasterDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchPermissions } = useRoles();
  const returnPage = (location.state as { page?: number } | undefined)?.page;

  const [isActive, setIsActive] = useState(false);
  const [loadingService, setLoadingService] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [service, setService] = useState<ServiceDetail | null>(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ServiceMasterCreationForm>({
    resolver: zodResolver(serviceMasterCreationSchema),
    defaultValues: {
      titulo: "",
      valorUnitario: "",
      categoria: [],
      unidadCotizacion: [],
      descripcion: "",
      cotizacionesDosis: [
        { modalidad: [], dosisDesde: "", dosisHasta: "", valorUnitario: "" },
      ],
    },
  });

  useEffect(() => {
    if (isActive && id) {
      fetchServiceHandler({
        id: Number(id),
        setLoadingService,
        setService,
        navigate,
        fetchPermissions,
      }).catch(() => {});
    }
  }, [isActive, id]);

  useEffect(() => {
    if (!service) return;

    reset(mapServiceDetailToFormValues(service));
  }, [service, reset]);

  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    return hasEmptyServiceMasterFormValues(watchedValues, true);
  }, [watchedValues]);

  const handleCancel = () => {
    navigate(AppRoutes.SERVICES, { state: { page: returnPage } });
  };

  const onSubmit = async (values: ServiceMasterCreationForm) => {
    if (!id) return;

    await updateServiceHandler({
      id: Number(id),
      values,
      setLoadingUpdate,
      navigate,
      fetchPermissions,
      returnPage,
    }).catch(() => {});
  };

  const handleDelete = async () => {
    if (!id) return;

    await deleteServiceHandler({
      id: Number(id),
      setLoadingDelete,
      navigate,
      fetchPermissions,
      returnPage,
    }).catch(() => {});
  };

  const loading = loadingService || loadingUpdate || loadingDelete;

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      flexDir="column"
      paddingY="20px"
      gap="12px"
    >
      <SectionHeader
        title="Editar servicio"
        addButtonLabel="Guardar"
        handleAdd={handleSubmit(onSubmit)}
        addButtonType="button"
        disableAdd={hasNoFilledValues || loading}
        loading={loading}
        aditionalButtons={
          <>
            <Button
              type="button"
              paddingX="12px"
              height="32px"
              bgColor={AppColors.RED_BUTTON_ALT}
              color="white"
              onClick={() => setOpenDeleteConfirm(true)}
              disabled={loading}
              data-testid="service-master-delete-button"
            >
              <Trash2 size={16} />
              <Text fontWeight={600} fontSize="14px">
                Eliminar
              </Text>
            </Button>
            <Button
              type="button"
              paddingX="12px"
              height="32px"
              onClick={handleCancel}
              data-testid="service-master-cancel-button"
            >
              <X size={16} />
              <Text fontWeight={600} fontSize="14px">
                Cancelar
              </Text>
            </Button>
          </>
        }
      />

      <ServiceMasterForm
        control={control}
        errors={errors}
        register={register}
        loading={loading}
        showStatusField
      />

      <ConfirmModal
        openConfirm={openDeleteConfirm}
        setOpenConfirm={setOpenDeleteConfirm}
        handleAccept={handleDelete}
        loadingAccept={loadingDelete}
        title="Eliminar servicio"
        subtitle="¿Desea eliminar este servicio? No podrá deshacer esta acción después."
      />
    </Flex>
  );
};
